import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Building, Copy, Folder, Globe, HardDrive, Mail, Trash2, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, type MailRecipientOption } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime, formatNumber, relativeTime } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import {
  DRIVE_KEY,
  dayOf,
  drivePage,
  endOfDay,
  useDriveRelatedOptions,
  type DriveContact,
  type DriveDepartmentOption,
  type DriveDetails,
  type DriveLink,
  type DriveRole,
  type DriveSharing,
} from '../lib/drive';
import { FormGrid, FormPage } from '../components/FormPage';
import { PeoplePicker } from '../components/PeoplePicker';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '../components/ui';

/**
 * Viganium Drive's forms — every one a page of its own (§ "every form is a
 * page"): a new folder, renaming and saying what an item is about, moving it,
 * sharing it, and emailing it to a client's contacts.
 */

/** The most people one item is shared with by name (DRIVE_MAX_PEOPLE). */
const MAX_PEOPLE = 200;
/** The most departments one item is shared with (DRIVE_MAX_DEPARTMENTS). */
const MAX_DEPARTMENTS = 100;
/** The most contacts one email goes to (DRIVE_MAX_CONTACTS). */
const MAX_CONTACTS = 20;

function useItem(id: string) {
  return useQuery({
    queryKey: [...DRIVE_KEY, 'item', id],
    queryFn: () => api.get<DriveDetails>(`/drive/items/${id}`),
    enabled: !!id,
  });
}

/** Back to where an item is shown: a folder's page, a file's page. */
const backTo = (item: DriveDetails | undefined) => (item ? drivePage(item) : '/drive');

// ---------------------------------------------------------------------------
// What it is about
// ---------------------------------------------------------------------------

/**
 * The client and the project an item is about, both optional. Picking a project
 * picks its client; picking a client keeps to its projects.
 */
function AboutFields({
  clientId,
  projectId,
  onChange,
}: {
  clientId: string;
  projectId: string;
  onChange: (next: { clientId: string; projectId: string }) => void;
}) {
  const { t, localized } = useI18n();
  const { data, isLoading } = useDriveRelatedOptions();
  const projects = (data?.projects ?? []).filter((p) => !clientId || p.clientId === clientId || p.id === projectId);
  return (
    <FormGrid>
      <Field label={t('drive.client')}>
        <Select
          value={clientId}
          disabled={isLoading}
          onChange={(e) => {
            const next = e.target.value;
            const project = data?.projects.find((p) => p.id === projectId);
            onChange({ clientId: next, projectId: project && next && project.clientId !== next ? '' : projectId });
          }}
        >
          <option value="">{t('drive.noClient')}</option>
          {(data?.clients ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {localized({ name: c.companyName, nameAr: c.companyNameAr })} · {c.code}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('drive.project')}>
        <Select
          value={projectId}
          disabled={isLoading}
          onChange={(e) => {
            const project = data?.projects.find((p) => p.id === e.target.value);
            onChange({ projectId: e.target.value, clientId: project?.clientId ?? clientId });
          }}
        >
          <option value="">{t('drive.noProject')}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {localized(p)} · {p.code}
            </option>
          ))}
        </Select>
      </Field>
      <p className="text-caption text-faint sm:col-span-2">{t('drive.aboutHint')}</p>
    </FormGrid>
  );
}

// ---------------------------------------------------------------------------
// New folder
// ---------------------------------------------------------------------------

/** `/drive/new-folder?parent=…` — or `?clientId=` / `?projectId=` from a client's or a project's Drive tab. */
export function DriveFolderFormPage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const parentId = params.get('parent') ?? '';
  const parent = useItem(parentId);
  const [name, setName] = useState('');
  const [about, setAbout] = useState({ clientId: params.get('clientId') ?? '', projectId: params.get('projectId') ?? '' });
  const [touched, setTouched] = useState(false);

  // A folder made in a folder is about what that folder is about, unless changed here.
  useEffect(() => {
    if (parent.data && !touched) setAbout({ clientId: parent.data.client?.id ?? '', projectId: parent.data.project?.id ?? '' });
  }, [parent.data, touched]);
  // A project picked on its Drive tab brings its client.
  const options = useDriveRelatedOptions();
  useEffect(() => {
    if (!about.projectId || about.clientId || !options.data) return;
    const project = options.data.projects.find((p) => p.id === about.projectId);
    if (project?.clientId) setAbout((a) => ({ ...a, clientId: project.clientId! }));
  }, [about, options.data]);

  const create = useMutation({
    mutationFn: () =>
      api.post<DriveDetails>('/drive/folders', {
        parentId: parentId || null,
        name,
        clientId: about.clientId || null,
        projectId: about.projectId || null,
      }),
    onSuccess: (folder) => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      toast.success(t('drive.folderCreated'));
      navigate(`/drive/folders/${folder.id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('drive.newFolder')}
      subtitle={t('drive.folderIn', { folder: parent.data?.name ?? t('drive.myDrive') })}
      backLabel={parent.data?.name ?? t('drive.title')}
      backTo={parentId ? `/drive/folders/${parentId}` : undefined}
      submitLabel={t('drive.createFolder')}
      onSubmit={() => create.mutate()}
      submitting={create.isPending}
      disabled={!name.trim()}
      loading={!!parentId && parent.isLoading}
      error={parentId ? parent.error : undefined}
    >
      <div className="space-y-5">
        <Field label={t('drive.name')} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} autoFocus />
        </Field>
        <AboutFields
          clientId={about.clientId}
          projectId={about.projectId}
          onChange={(next) => {
            setTouched(true);
            setAbout(next);
          }}
        />
      </div>
    </FormPage>
  );
}

// ---------------------------------------------------------------------------
// Rename and details
// ---------------------------------------------------------------------------

export function DriveEditPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const item = useItem(id);
  const [name, setName] = useState('');
  const [about, setAbout] = useState({ clientId: '', projectId: '' });

  useEffect(() => {
    if (!item.data) return;
    setName(item.data.name);
    setAbout({ clientId: item.data.client?.id ?? '', projectId: item.data.project?.id ?? '' });
  }, [item.data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<DriveDetails>(`/drive/items/${id}`, {
        name,
        clientId: about.clientId || null,
        projectId: about.projectId || null,
      }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      toast.success(t('drive.saved'));
      navigate(drivePage(saved));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const refused = item.data && !item.data.can.edit;
  return (
    <FormPage
      title={t('drive.edit')}
      subtitle={item.data?.name}
      backLabel={item.data?.name ?? t('drive.title')}
      backTo={backTo(item.data)}
      submitLabel={t('drive.save')}
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!name.trim() || !!refused}
      loading={item.isLoading}
      error={item.error ?? (refused ? new Error(t('drive.viewOnly')) : undefined)}
    >
      <div className="space-y-5">
        <Field label={t('drive.name')} required hint={item.data?.kind === 'FILE' ? t('drive.renameFileHint') : undefined}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} autoFocus />
        </Field>
        <AboutFields clientId={about.clientId} projectId={about.projectId} onChange={setAbout} />
      </div>
    </FormPage>
  );
}

// ---------------------------------------------------------------------------
// Move
// ---------------------------------------------------------------------------

interface MoveTargets {
  top: boolean;
  currentParentId: string | null;
  folders: Array<{ id: string; name: string; parentId: string | null; depth: number; path: string[] }>;
}

const TOP = '__top__';

export function DriveMovePage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const item = useItem(id);
  const targets = useQuery({
    queryKey: [...DRIVE_KEY, 'move-targets', id],
    queryFn: () => api.get<MoveTargets>(`/drive/items/${id}/move-targets`),
  });
  const [to, setTo] = useState('');

  // The folders as a tree, each under the one it is in.
  const ordered = useMemo(() => {
    const list = targets.data?.folders ?? [];
    const ids = new Set(list.map((f) => f.id));
    const children = new Map<string | null, typeof list>();
    for (const f of list) {
      const key = f.parentId && ids.has(f.parentId) ? f.parentId : null;
      children.set(key, [...(children.get(key) ?? []), f]);
    }
    const out: Array<(typeof list)[number] & { level: number }> = [];
    const walk = (parent: string | null, level: number) => {
      for (const f of children.get(parent) ?? []) {
        out.push({ ...f, level });
        walk(f.id, level + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [targets.data]);

  const move = useMutation({
    mutationFn: () => api.post<DriveDetails>(`/drive/items/${id}/move`, { parentId: to === TOP ? null : to }),
    onSuccess: (moved) => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      toast.success(t('drive.moved'));
      navigate(moved.parentId ? `/drive/folders/${moved.parentId}` : '/drive');
    },
    onError: (e: Error) => {
      const reason = e instanceof ApiError ? (e.details as { reason?: string } | undefined)?.reason : undefined;
      toast.error(reason ? t(`drive.moveRefused.${reason}` as TranslationKey) : e.message);
    },
  });

  const current = targets.data?.currentParentId ?? null;
  const choice = (value: string, label: ReactNode, level: number, here: boolean, hint?: string) => (
    <label
      key={value}
      className={clsx(
        'flex items-center gap-3 rounded-md px-3 py-2',
        here ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[var(--surface-sunken)]',
        to === value && 'bg-primary/10',
      )}
      style={{ paddingInlineStart: `${0.75 + level * 1.25}rem` }}
    >
      <input
        type="radio"
        name="destination"
        value={value}
        checked={to === value}
        disabled={here}
        onChange={() => setTo(value)}
        className="h-4 w-4 accent-primary"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-sm text-[var(--text-primary)]">{label}</span>
        {hint && <span className="block text-caption text-faint">{hint}</span>}
      </span>
    </label>
  );

  return (
    <FormPage
      title={t('drive.moveTitle', { name: item.data?.name ?? '' })}
      backLabel={item.data?.name ?? t('drive.title')}
      backTo={backTo(item.data)}
      submitLabel={t('drive.moveHere')}
      onSubmit={() => move.mutate()}
      submitting={move.isPending}
      disabled={!to}
      loading={item.isLoading || targets.isLoading}
      error={item.error ?? targets.error}
    >
      {!targets.data?.top && !ordered.length ? (
        <EmptyState icon={<Folder className="h-8 w-8" aria-hidden />} title={t('drive.noFolders')} hint="" />
      ) : (
        <div className="max-h-[60vh] space-y-0.5 overflow-y-auto" role="radiogroup" aria-label={t('drive.move')}>
          {targets.data?.top &&
            choice(
              TOP,
              <span className="inline-flex items-center gap-2">
                <HardDrive className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
                {t('drive.moveTop')}
              </span>,
              0,
              current === null,
              current === null ? t('drive.moveCurrent') : undefined,
            )}
          {ordered.map((f) =>
            choice(
              f.id,
              <span className="inline-flex items-center gap-2">
                <Folder className="h-4 w-4 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />
                {f.name}
              </span>,
              f.level + (targets.data?.top ? 1 : 0),
              f.id === current,
              f.id === current ? t('drive.moveCurrent') : undefined,
            ),
          )}
        </div>
      )}
    </FormPage>
  );
}

// ---------------------------------------------------------------------------
// Share
// ---------------------------------------------------------------------------

type Person = { userId: string; role: DriveRole };
type Team = { departmentId: string; role: DriveRole };

/**
 * An item's sharing — its owner's, and whoever's it is shared with to edit, and an
 * administrator's: people by name — any employee, whatever their department —
 * departments (whoever is in one now), and everyone, saved together; and, for
 * whoever may share outside the company, its public link and the links emailed
 * to a client's contacts, each changed on its own. Whoever shares it keeps their
 * own place on the list as it is, and shares a department of theirs no further
 * than they may themselves (the API says so when asked).
 */
export function DriveSharePage() {
  const { id = '' } = useParams();
  const { t, localized, locale } = useI18n();
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const item = useItem(id);
  const people = useQuery({
    queryKey: [...DRIVE_KEY, 'people'],
    queryFn: () => api.get<MailRecipientOption[]>('/drive/people'),
    staleTime: 60_000,
  });
  const departments = useQuery({
    queryKey: [...DRIVE_KEY, 'departments'],
    queryFn: () => api.get<DriveDepartmentOption[]>('/drive/departments'),
    staleTime: 60_000,
  });
  const [list, setList] = useState<Person[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [everyone, setEveryone] = useState<DriveRole | ''>('');
  const [dirty, setDirty] = useState(false);
  const sharing = item.data?.sharing;

  useEffect(() => {
    if (!sharing || dirty) return;
    setList(sharing.people.map((p) => ({ userId: p.user.id, role: p.role })));
    setTeams(sharing.departments.map((d) => ({ departmentId: d.department.id, role: d.role })));
    setEveryone(sharing.everyoneRole ?? '');
  }, [sharing, dirty]);

  const known = useMemo(() => {
    const map = new Map<string, MailRecipientOption | DriveSharing['people'][number]['user']>();
    for (const p of sharing?.people ?? []) map.set(p.user.id, p.user);
    for (const p of people.data ?? []) map.set(p.id, p);
    return map;
  }, [sharing, people.data]);
  // Never its owner, whose it is: an editor or an administrator sharing it is offered everyone else.
  const ownerId = item.data?.owner.id;
  const offered = useMemo(
    () => (people.data ?? []).filter((p) => p.id !== ownerId && !list.some((x) => x.userId === p.id)),
    [people.data, list, ownerId],
  );
  // A department on it stays named once it is no longer active, and so no longer offered.
  const knownTeams = useMemo(() => {
    const map = new Map<string, { name: string; nameAr: string | null; isActive: boolean; people?: number; mine?: boolean }>();
    for (const d of sharing?.departments ?? []) map.set(d.department.id, d.department);
    for (const d of departments.data ?? []) map.set(d.id, { ...d, isActive: true });
    return map;
  }, [sharing, departments.data]);
  const offeredTeams = (departments.data ?? []).filter((d) => !teams.some((x) => x.departmentId === d.id));

  const save = useMutation({
    mutationFn: () =>
      api.put<DriveSharing>(`/drive/items/${id}/shares`, { people: list, departments: teams, everyoneRole: everyone || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      setDirty(false);
      toast.success(t('drive.sharingSaved'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (item.isLoading) return <LoadingBlock />;
  if (item.error || !item.data) {
    return <ErrorState error={item.error ?? new Error(t('common.error'))} onRetry={() => void item.refetch()} />;
  }
  const data = item.data;
  if (!data.can.share || !sharing) {
    return <ErrorState error={new Error(t(data.binned ? 'drive.inTrashNotice' : 'drive.editorsShare'))} />;
  }
  const change = (next: Person[]) => {
    setDirty(true);
    setList(next);
  };
  const changeTeams = (next: Team[]) => {
    setDirty(true);
    setTeams(next);
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-4">
        <Link
          to={drivePage(data)}
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {data.name}
        </Link>
      </div>
      <PageHeader
        title={t('drive.shareTitle', { name: data.name })}
        subtitle={
          data.mine
            ? t('drive.shareSubtitle')
            : t(data.access === 'ADMIN' ? 'drive.shareSubtitleAdmin' : 'drive.shareSubtitleEditor', {
                name: localized(data.owner),
              })
        }
      />

      <div className="space-y-5">
        <Card>
          <CardHeader
            title={
              <span className="inline-flex items-center gap-2">
                <Users className="h-5 w-5 text-[var(--text-tertiary)]" aria-hidden />
                {t('drive.people')}
              </span>
            }
            subtitle={t('drive.peopleHint')}
          />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
            className="space-y-4"
          >
            <Field label={t('drive.addPerson')}>
              <PeoplePicker
                people={offered}
                value={[]}
                max={MAX_PEOPLE - list.length}
                onChange={(ids) => change([...list, ...ids.map((userId) => ({ userId, role: 'VIEW' as const }))])}
              />
            </Field>

            {list.length > 0 && (
              <ul className="divide-y divide-[var(--line)] rounded-md border hairline">
                {list.map((p) => {
                  const who = known.get(p.userId);
                  const inactive = who && 'isActive' in who && who.isActive === false;
                  // Whoever shares it keeps their own place as it is — the API keeps it so too.
                  const self = p.userId === user?.id;
                  return (
                    <li key={p.userId} className="flex flex-wrap items-center gap-3 px-3 py-2 sm:flex-nowrap">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-body-sm text-[var(--text-primary)]">{who ? localized(who) : '—'}</span>
                        {who?.jobTitle && <span className="block truncate text-caption text-faint">{who.jobTitle}</span>}
                      </span>
                      {self && <Badge tone="neutral">{t('drive.you')}</Badge>}
                      {inactive && <Badge tone="warning">{t('drive.inactive')}</Badge>}
                      <div className="w-36 shrink-0">
                        {self ? (
                          <span className="block px-3 text-body-sm text-muted" title={t('drive.ownPlace')}>
                            {t(p.role === 'EDIT' ? 'drive.roleEdit' : 'drive.roleView')}
                          </span>
                        ) : (
                          <Select
                            value={p.role}
                            onChange={(e) =>
                              change(list.map((x) => (x.userId === p.userId ? { ...x, role: e.target.value as DriveRole } : x)))
                            }
                            aria-label={t('drive.roleFor', { name: who ? localized(who) : '' })}
                          >
                            <option value="VIEW">{t('drive.roleView')}</option>
                            <option value="EDIT">{t('drive.roleEdit')}</option>
                          </Select>
                        )}
                      </div>
                      {self ? (
                        <span className="w-7 shrink-0" aria-hidden />
                      ) : (
                        <button
                          type="button"
                          onClick={() => change(list.filter((x) => x.userId !== p.userId))}
                          aria-label={t('drive.removePerson', { name: who ? localized(who) : '' })}
                          title={t('drive.remove')}
                          className="shrink-0 rounded-sm p-1.5 text-[var(--text-tertiary)] hover:bg-[var(--surface-sunken)] hover:text-error-fg"
                        >
                          <X className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <Field label={t('drive.departments')} hint={t('drive.departmentsHint')}>
              <div className="sm:w-72">
                <Select
                  value=""
                  disabled={!offeredTeams.length || teams.length >= MAX_DEPARTMENTS}
                  onChange={(e) => {
                    if (e.target.value) changeTeams([...teams, { departmentId: e.target.value, role: 'VIEW' }]);
                  }}
                  aria-label={t('drive.addDepartment')}
                >
                  <option value="">{t('drive.addDepartment')}</option>
                  {offeredTeams.map((d) => (
                    <option key={d.id} value={d.id}>
                      {localized(d)} ({formatNumber(d.people, locale)})
                    </option>
                  ))}
                </Select>
              </div>
            </Field>

            {teams.length > 0 && (
              <ul className="divide-y divide-[var(--line)] rounded-md border hairline">
                {teams.map((d) => {
                  const team = knownTeams.get(d.departmentId);
                  const name = team ? localized(team) : '—';
                  return (
                    <li key={d.departmentId} className="flex flex-wrap items-center gap-3 px-3 py-2 sm:flex-nowrap">
                      <Building className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                      {/* On a phone the name keeps the first line to itself; its way in and Remove wrap below. */}
                      <span className="min-w-0 grow basis-[calc(100%-1.75rem)] sm:basis-0">
                        <span className="block truncate text-body-sm text-[var(--text-primary)]">{name}</span>
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-faint">
                          {team?.people !== undefined && t('drive.departmentPeople', { n: formatNumber(team.people, locale) })}
                          {team?.mine && <Badge tone="neutral">{t('drive.yourDepartment')}</Badge>}
                          {team && !team.isActive && <Badge tone="warning">{t('drive.inactive')}</Badge>}
                        </span>
                      </span>
                      <div className="w-36 shrink-0">
                        <Select
                          value={d.role}
                          onChange={(e) =>
                            changeTeams(
                              teams.map((x) => (x.departmentId === d.departmentId ? { ...x, role: e.target.value as DriveRole } : x)),
                            )
                          }
                          aria-label={t('drive.roleFor', { name })}
                        >
                          <option value="VIEW">{t('drive.roleView')}</option>
                          <option value="EDIT">{t('drive.roleEdit')}</option>
                        </Select>
                      </div>
                      <button
                        type="button"
                        onClick={() => changeTeams(teams.filter((x) => x.departmentId !== d.departmentId))}
                        aria-label={t('drive.removePerson', { name })}
                        title={t('drive.remove')}
                        className="shrink-0 rounded-sm p-1.5 text-[var(--text-tertiary)] hover:bg-[var(--surface-sunken)] hover:text-error-fg"
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <Field label={t('drive.everyone')} hint={t('drive.roleHint')}>
              <div className="sm:w-72">
                <Select
                  value={everyone}
                  onChange={(e) => {
                    setDirty(true);
                    setEveryone(e.target.value as DriveRole | '');
                  }}
                >
                  <option value="">{t('drive.everyoneOff')}</option>
                  <option value="VIEW">{t('drive.everyoneView')}</option>
                  <option value="EDIT">{t('drive.everyoneEdit')}</option>
                </Select>
              </div>
            </Field>

            {sharing.through.length > 0 && (
              <ul className="space-y-1 text-caption text-muted">
                {sharing.through.map((f) => (
                  <li key={f.id}>
                    {t('drive.through', { folder: f.name })} —{' '}
                    {[
                      f.people ? t('drive.sharedPeople', { n: f.people }) : null,
                      f.departments ? t('drive.sharedDepartments', { n: f.departments }) : null,
                      f.everyoneRole ? t('drive.sharedEveryone') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}{' '}
                    {f.canShare && (
                      <Link to={`/drive/${f.id}/share`} className="text-primary-600 hover:underline dark:text-primary-400">
                        {t('drive.manageSharing')}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex justify-end">
              <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
                {t('drive.saveSharing')}
              </Button>
            </div>
          </form>
        </Card>

        {(data.can.shareOutside || sharing.publicLink) && (
          <PublicLinkCard item={data} link={sharing.publicLink} />
        )}
        {(data.can.shareOutside || sharing.contactLinks.length > 0) && (
          <ContactLinksCard item={data} links={sharing.contactLinks} />
        )}
        {!data.can.shareOutside && !sharing.publicLink && !sharing.contactLinks.length && (
          <p className="text-caption text-faint">{t('drive.outsideNeedsPermission')}</p>
        )}
      </div>
    </div>
  );
}

function copy(text: string, done: () => void) {
  void navigator.clipboard?.writeText(text).then(done, done);
}

/** How a link has been used: opened, downloaded, last when. */
function LinkStats({ link }: { link: DriveLink }) {
  const { t, locale } = useI18n();
  return (
    <span className="text-caption text-muted">
      {t('drive.linkStats', { opens: link.openCount, downloads: link.downloadCount })}
      {link.lastOpenedAt && ` · ${t('drive.lastOpened', { when: relativeTime(link.lastOpenedAt, locale) })}`}
    </span>
  );
}

function useStopLink(itemId: string) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asked, setAsked] = useState<string | null>(null);
  const stop = useMutation({
    mutationFn: (linkId: string) => api.post(`/drive/links/${linkId}/revoke`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...DRIVE_KEY, 'item', itemId] });
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      toast.success(t('drive.linkStopped'));
      setAsked(null);
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setAsked(null);
    },
  });
  const dialog = asked ? (
    <ConfirmDialog
      title={t('drive.confirmStopTitle')}
      confirmLabel={t('drive.stopLink')}
      danger
      busy={stop.isPending}
      onConfirm={() => stop.mutate(asked)}
      onCancel={() => setAsked(null)}
    >
      <p>{t('drive.confirmStopBody')}</p>
    </ConfirmDialog>
  ) : null;
  return { ask: setAsked, dialog };
}

/** Its public link: made, copied, given a last day, stopped. */
function PublicLinkCard({ item, link }: { item: DriveDetails; link: DriveLink | null }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [until, setUntil] = useState(dayOf(link?.expiresAt ?? null));
  const stop = useStopLink(item.id);
  useEffect(() => setUntil(dayOf(link?.expiresAt ?? null)), [link?.expiresAt]);

  const make = useMutation({
    mutationFn: (expiresAt: string | null) => api.post<DriveSharing>(`/drive/items/${item.id}/public-link`, { expiresAt }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      toast.success(link?.live ? t('drive.linkUpdated') : t('drive.linkCreated'));
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const live = link?.live;
  const today = dayOf(new Date().toISOString());

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Globe className="h-5 w-5 text-[var(--text-tertiary)]" aria-hidden />
            {t('drive.publicLink')}
          </span>
        }
        subtitle={t('drive.publicLinkHint')}
      />
      {live && link ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input readOnly value={link.url} dir="ltr" className="min-w-0 flex-1 font-mono text-caption" onFocus={(e) => e.target.select()} />
            <Button icon={<Copy className="h-4 w-4" aria-hidden />} onClick={() => copy(link.url, () => toast.success(t('drive.linkCopied')))}>
              {t('drive.copyLink')}
            </Button>
          </div>
          <LinkStats link={link} />
          <div>
            {/* The hint goes under the row, not the field, so the buttons sit level with the date. */}
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t('drive.availableUntil')} className="w-48">
                <Input type="date" value={until} min={today} onChange={(e) => setUntil(e.target.value)} />
              </Field>
              <Button
                loading={make.isPending}
                disabled={until === dayOf(link.expiresAt)}
                onClick={() => make.mutate(endOfDay(until))}
              >
                {t('drive.setEnd')}
              </Button>
              <div className="grow" />
              <Button variant="danger" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={() => stop.ask(link.id)}>
                {t('drive.stopLink')}
              </Button>
            </div>
            {link.expiresAt ? (
              <p className="mt-1 text-caption text-muted">{t('drive.endsOn', { date: formatDate(link.expiresAt, locale) })}</p>
            ) : (
              <p className="mt-1 text-caption text-faint">{t('drive.noEnd')}</p>
            )}
          </div>
        </div>
      ) : item.can.shareOutside ? (
        <div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('drive.availableUntil')} className="w-56">
              <Input type="date" value={until} min={today} onChange={(e) => setUntil(e.target.value)} />
            </Field>
            <Button variant="primary" loading={make.isPending} onClick={() => make.mutate(endOfDay(until))}>
              {t('drive.makePublicLink')}
            </Button>
          </div>
          <p className="mt-1 text-caption text-faint">{t('drive.untilHint')}</p>
          {link?.expired && <p className="mt-2 text-caption text-warning-fg dark:text-warning-dark">{t('drive.publicLinkExpired')}</p>}
        </div>
      ) : null}
      {stop.dialog}
    </Card>
  );
}

/** The links emailed to a client's contacts: each one's state, a copy, a stop — and sending to more. */
function ContactLinksCard({ item, links }: { item: DriveDetails; links: DriveLink[] }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const stop = useStopLink(item.id);
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Mail className="h-5 w-5 text-[var(--text-tertiary)]" aria-hidden />
            {t('drive.contacts')}
          </span>
        }
        subtitle={t('drive.contactsHint')}
        action={
          item.can.shareOutside ? (
            <Button variant="primary" onClick={() => navigate(`/drive/${item.id}/send`)}>
              {t('drive.sendToContacts')}
            </Button>
          ) : undefined
        }
      />
      {!links.length ? (
        <p className="text-body-sm text-muted">{t('drive.noContactLinks')}</p>
      ) : (
        <ul className="divide-y divide-[var(--line)]">
          {links.map((link) => (
            <li key={link.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-body-sm text-[var(--text-primary)]">
                  {link.name ?? link.contact?.name ?? '—'}
                  {link.contact && (
                    <span className="text-muted">
                      {' '}
                      · {localized({ name: link.contact.client.companyName, nameAr: link.contact.client.companyNameAr })}
                    </span>
                  )}
                </p>
                <p className="truncate text-caption text-faint" dir="ltr">
                  {link.email}
                </p>
                <p className="mt-0.5 flex flex-wrap gap-x-3 text-caption text-muted">
                  <span title={formatDateTime(link.createdAt, locale)}>{t('drive.sentOn', { when: relativeTime(link.createdAt, locale) })}</span>
                  {link.expiresAt && <span>{t('drive.endsOn', { date: formatDate(link.expiresAt, locale) })}</span>}
                  <LinkStats link={link} />
                </p>
              </div>
              <Badge tone={link.live ? 'success' : 'neutral'}>
                {link.live ? t('drive.linkLive') : link.contact?.removed ? t('drive.contactRemoved') : t('drive.linkExpired')}
              </Badge>
              {link.live && (
                <>
                  <Button size="sm" icon={<Copy className="h-3.5 w-3.5" aria-hidden />} onClick={() => copy(link.url, () => toast.success(t('drive.linkCopied')))}>
                    {t('drive.copyLink')}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => stop.ask(link.id)}>
                    {t('drive.stopLink')}
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {stop.dialog}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Email to a client's contacts
// ---------------------------------------------------------------------------

export function DriveSendPage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const item = useItem(id);
  const options = useDriveRelatedOptions();
  const [clientId, setClientId] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [until, setUntil] = useState('');
  const [language, setLanguage] = useState<'en' | 'ar'>('en');

  useEffect(() => {
    if (item.data?.client && !clientId) setClientId(item.data.client.id);
  }, [item.data, clientId]);

  const contacts = useQuery({
    queryKey: [...DRIVE_KEY, 'contacts', clientId],
    queryFn: () => api.get<DriveContact[]>(`/drive/clients/${clientId}/contacts`),
    enabled: !!clientId,
  });

  const send = useMutation({
    mutationFn: () =>
      api.post<{ email: { status: 'QUEUED' | 'OFF' | 'NOT_CONFIGURED'; count: number } }>(`/drive/items/${id}/send`, {
        clientId,
        contactIds: picked,
        message: message.trim() || undefined,
        expiresAt: endOfDay(until),
        locale: language,
      }),
    onSuccess: ({ email }) => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      if (email.status === 'QUEUED') toast.success(t('drive.sentQueued', { n: email.count }));
      else toast.toast(t(email.status === 'OFF' ? 'drive.sentOff' : 'drive.sentNotConfigured'), 'info');
      navigate(`/drive/${id}/share`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const refused = item.data && !item.data.can.shareOutside;
  const today = dayOf(new Date().toISOString());
  return (
    <FormPage
      title={t('drive.sendTitle', { name: item.data?.name ?? '' })}
      subtitle={t('drive.contactsHint')}
      backLabel={t('drive.share')}
      backTo={`/drive/${id}/share`}
      submitLabel={t('drive.send')}
      onSubmit={() => send.mutate()}
      submitting={send.isPending}
      disabled={!clientId || !picked.length || !!refused}
      loading={item.isLoading}
      error={item.error ?? (refused ? new Error(t('drive.outsideNeedsPermission')) : undefined)}
    >
      <div className="space-y-5">
        <Field label={t('drive.client')} required>
          <Select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setPicked([]);
            }}
          >
            <option value="">{t('drive.pickClient')}</option>
            {(options.data?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {localized({ name: c.companyName, nameAr: c.companyNameAr })} · {c.code}
              </option>
            ))}
          </Select>
        </Field>

        {clientId && (
          <Field label={t('drive.sendContacts')} required hint={t('drive.sendContactsHint', { n: MAX_CONTACTS })}>
            {contacts.isLoading ? (
              <LoadingBlock />
            ) : !contacts.data?.length ? (
              <p className="text-body-sm text-muted">{t('drive.noContacts')}</p>
            ) : (
              <ul className="space-y-1.5 rounded-md border hairline p-3">
                {contacts.data.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <Checkbox
                      checked={picked.includes(c.id)}
                      disabled={!c.email || (!picked.includes(c.id) && picked.length >= MAX_CONTACTS)}
                      onChange={(e) => setPicked(e.target.checked ? [...picked, c.id] : picked.filter((x) => x !== c.id))}
                      label={
                        <span>
                          <bdi dir="ltr">{c.name}</bdi>
                          {c.jobTitle && <span className="text-faint"> · <bdi dir="ltr">{c.jobTitle}</bdi></span>}
                        </span>
                      }
                    />
                    <span className="text-caption text-faint" dir="ltr">
                      {c.email ?? t('drive.noEmail')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Field>
        )}

        <Field label={t('drive.message')} hint={t('drive.messageHint')}>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={4} />
        </Field>

        <FormGrid>
          <Field label={t('drive.availableUntil')} hint={t('drive.untilHint')}>
            <Input type="date" value={until} min={today} onChange={(e) => setUntil(e.target.value)} />
          </Field>
          <Field label={t('drive.emailLanguage')} hint={t('drive.emailLanguageHint')}>
            <Select value={language} onChange={(e) => setLanguage(e.target.value as 'en' | 'ar')}>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </Select>
          </Field>
        </FormGrid>
      </div>
    </FormPage>
  );
}
